// E-mail na ficha do cliente: escrever (com template, anexos e retorno) e ver a conversa — specs/15-email.md §4.
import { ANEXOS_MAX, ANEXOS_MAX_BYTES, montarContexto, renderizar, renderizarEmail, WHATSAPP_BOTAO_PADRAO } from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, mensagemErro } from '../api';
import { useAuth } from '../auth';
import { Carregando, Erro, Icon, Modal } from '../components/ui';
import { dataHora, hojeISO } from '../format';
import { lerDataUrl } from '../imagem';
import { useMinhaCaixa } from './Configuracoes';
import { useTemplates } from './Templates';
import { corpoEmailFinal, corpoEmailVazio, EditorEmail, paraHtmlEmail, PreviaAssinatura, PreviaEmailHtml } from './EditorEmail';

export interface RespostaA { id: string; assunto: string }
interface ClienteEmail {
  id: string; nome: string; email: string | null; empresa?: string | null; cidade?: string | null;
  ultimaCompra?: string | null; valorUltimaCompra?: string | number | null;
}
interface Anexo { nome: string; tamanho: number; dataUrl: string }

const tamanho = (b: number) => (b >= 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const reAssunto = (s: string) => (/^re:/i.test(s) ? s : `Re: ${s}`);

export function ModalEmail({ cliente, respostaA, onClose, onEnviado }: {
  cliente: ClienteEmail; respostaA: RespostaA | null; onClose: () => void; onEnviado: (comRetorno: boolean) => void;
}) {
  const { me } = useAuth();
  const caixa = useMinhaCaixa();
  const templates = useTemplates('EMAIL');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [assunto, setAssunto] = useState(respostaA ? reAssunto(respostaA.assunto) : '');
  const [corpo, setCorpo] = useState(respostaA ? '<p>Olá {{primeiro_nome}},</p><p><br></p>' : '');
  const [aba, setAba] = useState<'escrever' | 'ver'>('escrever');
  // assinatura (texto + imagem) do vendedor; dá para tirar num envio (specs/25)
  const [comAssinatura, setComAssinatura] = useState(true);
  const [cc, setCc] = useState('');
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [retorno, setRetorno] = useState(!respostaA);
  // botão "Falar no WhatsApp" (specs/24): ligado quando o vendedor tem WhatsApp cadastrado
  const temWhats = !!me?.usuario.whatsapp;
  const [botaoWa, setBotaoWa] = useState(temWhats);
  const [dias, setDias] = useState(3);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (caixa.isLoading) return <Modal titulo="E-mail" onClose={onClose}><Carregando /></Modal>;
  const cx = caixa.data?.caixa;
  if (!cx || cx.status !== 'OK') {
    return (
      <Modal titulo="E-mail" onClose={onClose}>
        <h2 style={{ fontSize: 18 }}>Conecte sua caixa de e-mail</h2>
        <p className="muted" style={{ margin: 0 }}>
          {!cx ? 'Para enviar e-mails pelo CRM, cadastre a sua caixa de e-mail (o cliente recebe do seu endereço e a resposta volta para você).'
            : cx.status === 'FALHA' ? `Sua caixa está com falha: ${cx.ultimoErro ?? 'teste a conexão de novo'}.`
            : 'Sua caixa foi salva, mas ainda não passou no teste de conexão.'}
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Fechar</button>
          <Link className="btn primary" to="/configuracoes/email">Ir para Configurações</Link>
        </div>
      </Modal>
    );
  }

  const ctx = montarContexto({ cliente, vendedorNome: me?.usuario.nome, empresaVendedora: me?.empresa.nome, hoje: hojeISO() });
  const pa = renderizar(assunto, ctx);
  const corpoLimpo = corpoEmailFinal(corpo);
  const pc = renderizarEmail(corpoLimpo, ctx);
  const temAssinatura = !!(cx.assinatura || cx.assinaturaImagemUrl);
  const faltando = [...new Set([...pa.faltando, ...pc.faltando])];
  const totalAnexos = anexos.reduce((s, a) => s + a.tamanho, 0);
  const ccLista = cc.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
  const tpl = templates.data?.find((x) => x.id === templateId);
  const textoBotao = renderizar((tpl?.whatsappBotao && tpl.whatsappTexto) || WHATSAPP_BOTAO_PADRAO, ctx).texto;
  const limiteAtingido = caixa.data!.enviadosHoje >= caixa.data!.limiteDiario;

  const usarTemplate = (id: string) => {
    const t = templates.data?.find((x) => x.id === id);
    if (!t) return;
    setTemplateId(t.id);
    if (!respostaA) setAssunto(t.assunto ?? '');
    setCorpo(paraHtmlEmail(t.corpo));
    // template com botão WhatsApp: liga o botão (texto, mensagem e status vêm do template, specs/24)
    if (t.whatsappBotao) setBotaoWa(true);
  };

  async function adicionar(arquivos: FileList | null) {
    if (!arquivos) return;
    setErro(null);
    const novos: Anexo[] = [];
    for (const f of Array.from(arquivos)) {
      if (anexos.length + novos.length >= ANEXOS_MAX) { setErro(`No máximo ${ANEXOS_MAX} anexos.`); break; }
      novos.push({ nome: f.name, tamanho: f.size, dataUrl: await lerDataUrl(f) });
    }
    const total = [...anexos, ...novos].reduce((s, a) => s + a.tamanho, 0);
    if (total > ANEXOS_MAX_BYTES) return setErro('Os anexos passam de 5 MB no total.');
    setAnexos([...anexos, ...novos]);
  }

  async function enviar() {
    setErro(null);
    setEnviando(true);
    try {
      await api(`/clientes/${cliente.id}/emails`, {
        body: {
          assunto, corpo: corpoLimpo, cc: ccLista, assinatura: comAssinatura,
          ...(templateId ? { templateId } : {}),
          ...(respostaA ? { respostaA: respostaA.id } : {}),
          ...(retorno ? { retornoDias: dias } : {}),
          ...(botaoWa && temWhats ? { botaoWhatsapp: true } : {}),
          anexos: anexos.map((a) => ({ nome: a.nome, dataUrl: a.dataUrl })),
        },
      });
      onEnviado(retorno);
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal titulo="Novo e-mail" onClose={onClose} largo>
      <h2 style={{ fontSize: 18 }}>{respostaA ? 'Responder' : 'E-mail para'} {cliente.nome}</h2>
      <dl className="dados" style={{ gridTemplateColumns: '90px minmax(0, 1fr)', columnGap: 12 }}>
        <dt style={{ paddingTop: 2 }}>De</dt><dd className="num" style={{ marginBottom: 6 }}>{cx.nomeRemetente} &lt;{cx.email}&gt;</dd>
        <dt style={{ paddingTop: 2 }}>Para</dt><dd className="num" style={{ marginBottom: 6 }}>{cliente.email}</dd>
      </dl>
      <label className="field"><span>Cc (opcional)</span><input className="input num" value={cc} placeholder="financeiro@cliente.com.br" onChange={(e) => setCc(e.target.value)} /></label>

      {!!templates.data?.length && (
        <label className="field"><span>Template</span>
          <select className="select" value={templateId ?? ''} onChange={(e) => (e.target.value ? usarTemplate(e.target.value) : setTemplateId(null))}>
            <option value="">Escrever do zero</option>
            {templates.data.map((t) => <option key={t.id} value={t.id}>{t.nome}{t.categoria ? ` · ${t.categoria}` : ''}</option>)}
          </select>
        </label>
      )}
      <label className="field"><span>Assunto *</span><input className="input" value={assunto} maxLength={200} onChange={(e) => setAssunto(e.target.value)} /></label>
      <div className="tabs" role="tablist" aria-label="Mensagem">
        <button type="button" role="tab" className={`tab${aba === 'escrever' ? ' on' : ''}`} aria-selected={aba === 'escrever'} onClick={() => setAba('escrever')}>Escrever</button>
        <button type="button" role="tab" className={`tab${aba === 'ver' ? ' on' : ''}`} aria-selected={aba === 'ver'} onClick={() => setAba('ver')}>Como o cliente recebe</button>
      </div>
      <div style={aba === 'escrever' ? undefined : { display: 'none' }}>
        <EditorEmail valor={corpo} onChange={setCorpo} rotulo="Mensagem" />
      </div>
      {aba === 'ver' && (
        <PreviaEmailHtml html={corpoLimpo} ctx={ctx} assunto={pa.texto}>
          {botaoWa && temWhats && <span className="previa-botao-wa">{textoBotao}</span>}
          {comAssinatura && <PreviaAssinatura texto={cx.assinatura} imagemUrl={cx.assinaturaImagemUrl} largura={cx.assinaturaImagemLargura} posicao={cx.assinaturaImagemPosicao} />}
        </PreviaEmailHtml>
      )}
      {faltando.length > 0 && <div className="alert warn" role="status"><Icon name="alerta" /><span>Este cliente não tem: {faltando.join(', ')}. Edite o texto ou use {'{{variavel|texto padrão}}'}.</span></div>}

      <div className="col" style={{ gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label className="btn sm">
            <Icon name="anexo" size={14} />Anexar arquivo
            <input type="file" multiple hidden onChange={(e) => { void adicionar(e.target.files); e.target.value = ''; }} />
          </label>
          <span className="small faint">até {ANEXOS_MAX} arquivos, 5 MB no total</span>
        </div>
        {anexos.map((a, i) => (
          <span key={`${a.nome}-${i}`} className="badge" style={{ alignSelf: 'flex-start', gap: 6, alignItems: 'center' }}>
            {a.nome} · {tamanho(a.tamanho)}
            <button type="button" className="link-btn" style={{ fontSize: 12 }} aria-label={`Remover ${a.nome}`} onClick={() => setAnexos(anexos.filter((_, j) => j !== i))}>×</button>
          </span>
        ))}
        {totalAnexos > 0 && <span className="small faint num">Total: {tamanho(totalAnexos)}</span>}
      </div>

      <label className="check" title={temWhats ? undefined : 'Cadastre seu WhatsApp em Minha conta'}>
        <input type="checkbox" checked={botaoWa && temWhats} disabled={!temWhats} onChange={(e) => setBotaoWa(e.target.checked)} />
        <span>
          Botão “{textoBotao}”
          {!temWhats && <> · <Link to="/conta">cadastre seu WhatsApp</Link></>}
        </span>
      </label>
      {temAssinatura && (
        <label className="check"><input type="checkbox" checked={comAssinatura} onChange={(e) => setComAssinatura(e.target.checked)} />Incluir minha assinatura{cx.assinaturaImagemUrl ? ' (com imagem)' : ''}</label>
      )}
      <label className="check">
        <input type="checkbox" checked={retorno} onChange={(e) => setRetorno(e.target.checked)} />
        Se não responder, criar retorno em
        <select className="select" style={{ width: 'auto', height: 30 }} value={dias} disabled={!retorno} onChange={(e) => setDias(Number(e.target.value))} aria-label="Dias">
          {[1, 2, 3, 5, 7, 15].map((d) => <option key={d} value={d}>{d} {d === 1 ? 'dia' : 'dias'}</option>)}
        </select>
      </label>

      <Erro erro={erro} />
      {limiteAtingido && <div className="alert warn"><Icon name="alerta" /><span>Você atingiu o limite de {caixa.data!.limiteDiario} e-mails hoje.</span></div>}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="small faint" style={{ marginRight: 'auto' }}>Fica registrado na timeline do cliente.</span>
        <button className="btn" onClick={onClose}>Cancelar</button>
        <button className="btn primary" disabled={enviando || !assunto.trim() || corpoEmailVazio(corpoLimpo) || faltando.length > 0 || limiteAtingido} onClick={enviar}>
          <Icon name="email" size={15} />{enviando ? 'Enviando…' : 'Enviar e-mail'}
        </button>
      </div>
    </Modal>
  );
}

interface EmailItem {
  id: string; direcao: 'ENVIADO' | 'RECEBIDO'; de: string; para: string; cc: string | null; assunto: string; corpo: string;
  anexos: Array<{ nome: string; tamanho: number }>; createdAt: string; usuarioNome: string;
}

export function ModalConversa({ clienteId, podeResponder, onClose, onResponder }: {
  clienteId: string; podeResponder: boolean; onClose: () => void; onResponder: (r: RespostaA) => void;
}) {
  const q = useQuery({ queryKey: ['emails', clienteId], queryFn: () => api<EmailItem[]>(`/clientes/${clienteId}/emails`) });
  return (
    <Modal titulo="Conversa por e-mail" onClose={onClose} largo>
      <div className="card-head"><h2 style={{ fontSize: 18 }}>Conversa por e-mail</h2><button className="btn sm" onClick={onClose}>Fechar</button></div>
      {q.isLoading ? <Carregando /> : q.error ? <Erro erro={mensagemErro(q.error)} /> : !q.data?.length ? <div className="empty">Nenhum e-mail ainda.</div> : (
        <div className="conversa">
          {q.data.map((e) => (
            <article key={e.id} className={e.direcao === 'RECEBIDO' ? 'recebido' : undefined}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className={`badge ${e.direcao === 'RECEBIDO' ? 'green' : 'blue'}`}>{e.direcao === 'RECEBIDO' ? 'Recebido' : 'Enviado'}</span>
                <strong style={{ fontSize: 13.5, fontWeight: 600 }}>{e.assunto}</strong>
                <span className="small faint num" style={{ marginLeft: 'auto' }}>{dataHora(e.createdAt)}</span>
              </div>
              <span className="small muted num">{e.direcao === 'RECEBIDO' ? `De ${e.de}` : `${e.usuarioNome} → ${e.para}${e.cc ? ` · cc ${e.cc}` : ''}`}</span>
              <div className="corpo">{e.corpo}</div>
              {e.anexos.length > 0 && <span className="small faint">Anexos: {e.anexos.map((a) => a.nome).join(', ')}</span>}
              {e.direcao === 'RECEBIDO' && podeResponder && (
                <div><button className="btn sm primary" onClick={() => onResponder({ id: e.id, assunto: e.assunto })}><Icon name="responder" size={14} />Responder</button></div>
              )}
            </article>
          ))}
        </div>
      )}
    </Modal>
  );
}
